import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/client';

export default function AuditPage() {
  const { auctionId } = useParams<{ auctionId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!auctionId) return;
    api.get(`/auctions/${auctionId}/results-audit`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail ?? 'Failed to load audit'))
      .finally(() => setLoading(false));
  }, [auctionId]);

  function toggle(key: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  if (loading) return <div style={s.loading}>Loading audit…</div>;
  if (error)   return <div style={s.error}>{error}</div>;
  if (!data)   return null;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate('/auctioneer')}>← Back</button>
        <h2 style={s.title}>Allocation Audit</h2>
      </div>

      {/* Meta panel */}
      <div style={s.meta}>
        <MetaItem label="Auction ID"    value={data.auctionId} mono />
        <MetaItem label="Status"        value={data.auctionStatus} />
        <MetaItem label="Final Round"   value={data.finalRoundNumber} />
        <MetaItem label="Total Revenue" value={`$${data.totalRevenue.toFixed(2)}`} />
        <MetaItem label="Seed Source"   value={data.seedSource}
                  badge={data.seedSource === 'stored' ? 'green' : 'orange'} />
        <MetaItem label="Seed"          value={data.seedUsed} mono />
        <MetaItem label="Persisted Allocation" value={data.persistedAllocationExists ? 'Yes' : 'No'} />
        <MetaItem label="Generated At"  value={new Date(data.auditGeneratedAt).toLocaleString()} />
      </div>

      {/* Categories */}
      {data.categories.map((cat: any) => (
        <div key={cat.categoryId} style={s.catCard}>
          <div style={s.catHeader}>
            <span style={s.catName}>{cat.categoryName}</span>
            <span style={s.catPill}>Supply {cat.supply}</span>
            <span style={s.catPill}>Clock ${cat.clockPrice.toFixed(2)}</span>
            <span style={s.catPill}>Clock allocated {cat.totalClockAllocated}</span>
            <span style={s.catPill}>Residual {cat.residualSupply}</span>
            <span style={s.catPill}>Ext allocated {cat.totalSelectedExtensionQuantity}</span>
            <span style={s.catPill}>Cat revenue ${cat.categoryRevenue.toFixed(2)}</span>
            <span style={{
              ...s.catPill,
              background: cat.selectionMode === 'optimized' ? '#d4edda' : '#fff3cd',
              color: cat.selectionMode === 'optimized' ? '#155724' : '#856404',
            }}>
              {cat.optimizerReason}
            </span>
          </div>

          {/* Bidder table */}
          <table style={s.table}>
            <thead>
              <tr>
                {['Bidder','Clock Qty','Chains','Options','Selected Opt','Ext Qty','Ext Rev'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cat.bidders.map((b: any, bi: number) => {
                const selOpt = b.optionsSentToOptimizer[b.selectedOptionIndex];
                const key = `${cat.categoryId}:${b.bidderId}`;
                const open = expanded.has(key);
                return (
                  <React.Fragment key={b.bidderId}>
                    <tr
                      style={{ ...s.tr, background: bi % 2 === 0 ? '#fff' : '#f9f9f9', cursor: 'pointer' }}
                      onClick={() => toggle(key)}
                    >
                      <td style={s.td}><strong>{b.bidderName}</strong></td>
                      <td style={s.tdC}>{b.clockQuantity}</td>
                      <td style={s.tdC}>
                        {b.chainCount > 0
                          ? <span style={s.link}>{b.chainCount} {open ? '▲' : '▼'}</span>
                          : <span style={s.dim}>—</span>}
                      </td>
                      <td style={s.tdC}>{b.optionsSentToOptimizer.length}</td>
                      <td style={s.tdC}>
                        <span style={{
                          ...s.badge,
                          background: b.selectedOptionIndex === 0 ? '#e9ecef' : '#cce5ff',
                          color: b.selectedOptionIndex === 0 ? '#495057' : '#004085',
                        }}>
                          #{b.selectedOptionIndex}
                        </span>
                      </td>
                      <td style={s.tdC}>{selOpt.additionalQuantity}</td>
                      <td style={s.tdC}>${selOpt.additionalRevenue.toFixed(2)}</td>
                    </tr>
                    {open && (
                      <tr style={{ background: '#f0f4ff' }}>
                        <td colSpan={7} style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>

                            {/* Retained rungs */}
                            {b.retainedRungs.length > 0 && (
                              <div>
                                <div style={s.subHead}>Retained Rungs</div>
                                <table style={s.subTable}>
                                  <thead>
                                    <tr>{['From','To','Incr','Price','Kind'].map(h =>
                                      <th key={h} style={s.subTh}>{h}</th>)}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {b.retainedRungs.map((r: any) => (
                                      <tr key={r.id}>
                                        <td style={s.subTd}>{r.fromQuantity}</td>
                                        <td style={s.subTd}>{r.toQuantity}</td>
                                        <td style={s.subTd}>{r.incrementQuantity}</td>
                                        <td style={s.subTd}>${r.bidPrice.toFixed(2)}</td>
                                        <td style={s.subTd}>
                                          <span style={{
                                            ...s.badge,
                                            background: r.exitBidKind === 'FULL' ? '#d4edda' : '#fff3cd',
                                            color: r.exitBidKind === 'FULL' ? '#155724' : '#856404',
                                          }}>{r.exitBidKind}</span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Chains */}
                            {b.chains.length > 0 && (
                              <div>
                                <div style={s.subHead}>Extension Chains</div>
                                <table style={s.subTable}>
                                  <thead>
                                    <tr>{['Path','Addl Qty','Addl Rev'].map(h =>
                                      <th key={h} style={s.subTh}>{h}</th>)}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {b.chains.map((c: any, ci: number) => (
                                      <tr key={ci}>
                                        <td style={s.subTd}>
                                          <span style={s.mono}>{c.pathQuantities.join(' → ')}</span>
                                        </td>
                                        <td style={s.subTd}>{c.additionalQuantity}</td>
                                        <td style={s.subTd}>${c.additionalRevenue.toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Options sent to optimizer */}
                            <div>
                              <div style={s.subHead}>Options → Optimizer</div>
                              <table style={s.subTable}>
                                <thead>
                                  <tr>{['#','Addl Qty','Addl Rev','Tie-Break','Selected'].map(h =>
                                    <th key={h} style={s.subTh}>{h}</th>)}
                                  </tr>
                                </thead>
                                <tbody>
                                  {b.optionsSentToOptimizer.map((o: any) => {
                                    const isSel = o.optionIndex === b.selectedOptionIndex;
                                    return (
                                      <tr key={o.optionIndex} style={{
                                        background: isSel ? '#cce5ff' : undefined,
                                        fontWeight: isSel ? 600 : undefined,
                                      }}>
                                        <td style={s.subTd}>{o.optionIndex}</td>
                                        <td style={s.subTd}>{o.additionalQuantity}</td>
                                        <td style={s.subTd}>${o.additionalRevenue.toFixed(2)}</td>
                                        <td style={{ ...s.subTd, ...s.mono }}>{o.tieBreak.toFixed(6)}</td>
                                        <td style={{ ...s.subTd, textAlign: 'center' }}>{isSel ? '✓' : ''}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function MetaItem({ label, value, mono, badge }: {
  label: string; value: any; mono?: boolean; badge?: 'green' | 'orange';
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      {badge ? (
        <span style={{
          fontSize: '0.8rem', fontWeight: 600, padding: '2px 8px', borderRadius: '12px',
          background: badge === 'green' ? '#d4edda' : '#fff3cd',
          color: badge === 'green' ? '#155724' : '#856404',
          display: 'inline-block',
        }}>{value}</span>
      ) : (
        <span style={{ fontSize: '0.85rem', fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>{String(value)}</span>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:     { minHeight: '100vh', background: '#f0f2f5', paddingBottom: '3rem' },
  header:   { background: '#1a1a2e', color: 'white', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1.5rem' },
  back:     { background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: 'white', borderRadius: '4px', padding: '0.4rem 0.8rem', cursor: 'pointer' },
  title:    { margin: 0, fontSize: '1.2rem' },
  loading:  { padding: '3rem', textAlign: 'center', color: '#666' },
  error:    { padding: '3rem', textAlign: 'center', color: '#c0392b', fontWeight: 600 },
  meta:     { background: 'white', margin: '1.5rem 2rem 0', padding: '1.25rem 1.5rem', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', flexWrap: 'wrap', gap: '1.5rem' },
  catCard:  { background: 'white', margin: '1.5rem 2rem 0', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  catHeader:{ background: '#1a1a2e', color: 'white', padding: '0.75rem 1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' },
  catName:  { fontWeight: 700, fontSize: '1rem', marginRight: '0.5rem' },
  catPill:  { background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78rem' },
  table:    { width: '100%', borderCollapse: 'collapse' },
  th:       { padding: '0.6rem 0.75rem', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', textAlign: 'left', fontSize: '0.8rem', color: '#495057', fontWeight: 600 },
  tr:       { borderBottom: '1px solid #f0f0f0' },
  td:       { padding: '0.6rem 0.75rem', fontSize: '0.85rem' },
  tdC:      { padding: '0.6rem 0.75rem', fontSize: '0.85rem', textAlign: 'center' },
  link:     { color: '#4a90e2', fontWeight: 600 },
  dim:      { color: '#aaa' },
  badge:    { borderRadius: '10px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 },
  subHead:  { fontWeight: 600, fontSize: '0.78rem', color: '#495057', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  subTable: { borderCollapse: 'collapse', fontSize: '0.8rem' },
  subTh:    { padding: '4px 10px', background: '#e9ecef', borderBottom: '1px solid #dee2e6', textAlign: 'left', fontWeight: 600, color: '#495057' },
  subTd:    { padding: '4px 10px', borderBottom: '1px solid #f0f0f0' },
  mono:     { fontFamily: 'monospace', fontSize: '0.8rem' },
};
